import { Template } from '@core/models/template';
import { NativeDateAdapter } from '@angular/material/core';
import { FormGroup } from '@angular/forms';
import { format } from 'date-fns';
import { getDateMonthsAgo, getFirstDayOfMonth } from '../utils';

export class CustomDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: unknown): string {
    if (displayFormat === 'input') {
      return format(date, 'MM/yyyy');
    }
    return format(date, 'MMM yyyy');
  }
}

export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'input',
  },
  display: {
    dateInput: 'input',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

export const handleRangeMonthSelection = (
  date: Date,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  picker: any,
  form: FormGroup,
  startControlName: string,
  endControlName: string,
  markForCheck: () => void
): void => {
  const startDate = form.get(startControlName)?.value;
  const endDate = form.get(endControlName)?.value;

  if (!startDate || (startDate && endDate)) {
    const start = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0));
    form.patchValue({ [startControlName]: start, [endControlName]: null });
    markForCheck();
    picker._destroyOverlay();
    setTimeout(() => {
      if (!form.get(endControlName)?.value) {
        picker.open();
      }
    }, 100);
  } else {
    const lastDay = new Date(Date.UTC(date.getFullYear(), date.getMonth() + 1, 0)).getUTCDate();
    const end = new Date(Date.UTC(date.getFullYear(), date.getMonth(), lastDay, 23, 59, 59, 999));
    form.patchValue({ [endControlName]: end });
    picker._destroyOverlay();
    markForCheck();
  }
};

/**
 * Get Data Collection Template name by template ID.
 *
 * @function
 * @param {Template[]} templatesList - templates list.
 * @param {string} templateId - template ID.
 * @returns {string} - template name.
 */
export const getDataCollectionTemplateName = (
  templatesList: Template[],
  templateId: string
): string => {
  const template = templatesList.find((template) => template.id === templateId);

  return template?.name ? template.name : '';
};

/**
 * Get the first day of the reporting month for emissions based on a specified number of months ago.
 *
 * @function
 * @param {number} monthAgo - The number of months ago to retrieve the reporting month.
 * @returns {string} - An ISO string representing the first day of the reporting month.
 * @example "2023-01-01T00:00:00.000Z"
 */
export const getEmissionsFirstDayOfReportingMonth = (monthAgo: number): string => {
  const dateMonthAgo = getDateMonthsAgo(monthAgo);
  const year = dateMonthAgo.getFullYear();
  const month = dateMonthAgo.getMonth();

  return getFirstDayOfMonth(year, month);
};

/**
 * Get the last day of the reporting month for emissions based on a specified number of months ago.
 *
 * @function
 * @param {number} monthAgo - The number of months ago to retrieve the reporting month.
 * @returns {string} - An ISO string representing the last day of the reporting month.
 * @example "2023-11-30T23:59:59.999Z"
 */
export const getEmissionsLastDayOfReportingMonth = (monthAgo: number): string => {
  const date = new Date(getEmissionsFirstDayOfReportingMonth(monthAgo));
  date.setMilliseconds(date.getMilliseconds() - 1);

  return date.toISOString();
};
